import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import logo from "../assets/logo.png";
import API_BASE_URL from "../config";
import {
  FaHome,
  FaSearch,
  FaSlidersH,
  FaSignOutAlt,
  FaUserCog,
  FaChevronDown,
  FaExchangeAlt,
} from "react-icons/fa";

/* ─────────────── styles ─────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');

  .mnav * { font-family: 'DM Sans', sans-serif; box-sizing: border-box; }

  /* ── top bar ── */
  .mnav-topbar {
    background: #ffffff;
    border-bottom: 1px solid #eef0f4;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 8px;
    height: 58px;
    position: relative;
    z-index: 1100;
  }

  /* ── main navbar ── */
  .mnav-bar {
    height: 48px;
    display: flex;
    align-items: center;
    padding: 0 24px;
    position: relative;
    z-index: 1050;
    transition: background-color 0.3s ease;
  }
  .mnav-bar::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 1px;
    background: rgba(255,255,255,0.08);
  }

  /* ── nav item ── */
  .mnav-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 0 14px;
    height: 46px;
    color: #ffffff;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.2px;
    cursor: pointer;
    text-decoration: none;
    transition: color 0.18s ease;
    white-space: nowrap;
    user-select: none;
  }
  .mnav-item:hover { color: #ffffff; }
  .mnav-item::before {
    content: '';
    position: absolute;
    bottom: 0; left: 14px; right: 14px;
    height: 2px;
    background: rgba(255,255,255,0);
    border-radius: 2px 2px 0 0;
    transition: background 0.18s ease;
  }
  .mnav-item:hover::before,
  .mnav-item.active::before { background: rgba(255,255,255,0.7); }
  .mnav-item .chevron {
    font-size: 10px;
    opacity: 0.7;
    margin-left: 1px;
    transition: transform 0.2s ease, opacity 0.2s;
  }
  .mnav-item:hover .chevron { transform: rotate(180deg); opacity: 1; }

  /* ── dropdown panel ── */
  .mnav-drop {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    min-width: 190px;
    background: #ffffff;
    border: 1px solid #e8eaf0;
    border-radius: 10px;
    box-shadow: 0 12px 36px rgba(8,2,53,0.13), 0 2px 8px rgba(8,2,53,0.07);
    overflow: hidden;
    animation: mdropIn 0.17s ease;
    z-index: 2000;
  }
  @keyframes mdropIn {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .mnav-drop a {
    display: block;
    padding: 9px 16px;
    font-size: 13px;
    font-weight: 500;
    color: #2d3142;
    text-decoration: none;
    transition: background 0.13s, color 0.13s;
    border-left: 3px solid transparent;
    cursor: pointer;
  }
  .mnav-drop a:hover {
    background: #f5f6ff;
    color: #080235;
    border-left-color: #5b5fc7;
  }

  /* ── search ── */
  .mnav-search {
    display: flex;
    align-items: center;
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: 22px;
    padding: 0 14px;
    height: 32px;
    gap: 8px;
    transition: background 0.2s, border 0.2s, width 0.3s ease;
    width: 180px;
    margin-left: 12px;
  }
  .mnav-search:focus-within {
    background: rgba(255,255,255,0.2);
    border-color: rgba(255,255,255,0.4);
    width: 220px;
  }
  .mnav-search input {
    background: transparent;
    border: none;
    outline: none;
    color: #fff;
    font-size: 13px;
    width: 100%;
  }
  .mnav-search input::placeholder { color: rgba(255,255,255,0.55); }
  .mnav-search svg { color: rgba(255,255,255,0.6); flex-shrink: 0; }

  /* ── profile btn ── */
  .mnav-profile-btn {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    padding: 6px 10px;
    border-radius: 10px;
    transition: background 0.15s;
  }
  .mnav-profile-btn:hover { background: #f4f5fb; }
  .mnav-avatar {
    width: 36px; height: 36px;
    border-radius: 50%;
    border: 2px solid #e8eaf0;
    object-fit: cover;
    flex-shrink: 0;
  }
  .mnav-avatar-fallback {
    width: 36px; height: 36px;
    border-radius: 50%;
    border: 2px solid #e8eaf0;
    background: #e8eaf0;
    display: flex; align-items: center; justify-content: center;
    font-weight: 700; font-size: 14px; color: #374151;
    flex-shrink: 0;
  }
  .mnav-profile-name { font-size: 14px; font-weight: 600; color: #1a1d2e; line-height: 1.2; }
  .mnav-profile-role { font-size: 11px; font-weight: 500; color: #8b90a7; }

  /* ── profile dropdown ── */
  .mnav-profile-drop {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    min-width: 220px;
    background: #fff;
    border: 1px solid #e8eaf0;
    border-radius: 12px;
    box-shadow: 0 16px 40px rgba(8,2,53,0.13);
    overflow: hidden;
    animation: mdropIn 0.17s ease;
    z-index: 3000;
  }
  .mnav-profile-header {
    padding: 14px 16px 10px;
    background: linear-gradient(135deg, #f8f9ff 0%, #eef0fb 100%);
    border-bottom: 1px solid #e8eaf0;
  }
  .mnav-profile-header .name { font-size: 14px; font-weight: 700; color: #1a1d2e; }
  .mnav-profile-header .role-badge {
    display: inline-block;
    margin-top: 4px;
    padding: 2px 10px;
    font-size: 10.5px;
    font-weight: 600;
    border-radius: 20px;
    background: #080235;
    color: #fff;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }
  .mnav-profile-drop .menu-item {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 16px;
    font-size: 13px; font-weight: 500;
    color: #2d3142;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.13s;
  }
  .mnav-profile-drop .menu-item:hover { background: #f5f6ff; }
  .mnav-profile-drop .menu-item.danger { color: #dc2626; }
  .mnav-profile-drop .menu-item.danger:hover { background: #fff5f5; }
  .mnav-profile-drop .divider { height: 1px; background: #f0f1f5; margin: 4px 0; }

  /* ── settings / home btns ── */
  .mnav-settings-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 6px 14px;
    border-radius: 8px;
    font-size: 15px; font-weight: 600;
    color: #444b6b;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.15s, color 0.15s;
  }
  .mnav-settings-btn:hover { background: #f0f1f8; color: #080235; }
  .mnav-home-btn {
    display: flex; align-items: center;
    padding: 7px 10px;
    border-radius: 8px;
    color: #6b7394;
    text-decoration: none;
    transition: background 0.15s, color 0.15s;
  }
  .mnav-home-btn:hover { background: #f0f1f8; color: #080235; }

  /* ── role pills ── */
  .role-pill {
    display: inline-block;
    padding: 4px 12px;
    font-size: 11.5px; font-weight: 600;
    border-radius: 20px;
    background: #f0f1f8;
    color: #3a4060;
    cursor: pointer;
    margin: 2px 0;
    transition: background 0.15s, color 0.15s;
    text-decoration: none;
  }
  .role-pill:hover { background: #080235; color: #fff; }
`;

/* ─────────────── component ─────────────── */
const ManagerNavbar = () => {
  const navigate = useNavigate();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [searchVal, setSearchVal] = useState("");
  const [photoError, setPhotoError] = useState(false);
  const [profile, setProfile] = useState({
    username: "Manager",
    role: "MANAGER",
    roles: [],
    photo: null,
  });
  const [navbarColor, setNavbarColor] = useState("#080235");

  const userId = localStorage.getItem("userId");
  const employeeId = localStorage.getItem("employeeId");
  const managerId = localStorage.getItem("employeeId");

  const activeRole =
    localStorage.getItem("activeRole") ||
    (profile.role ? `ROLE_${profile.role.toUpperCase()}` : null);

  const open = (key) => setOpenDropdown(key);
  const close = () => setOpenDropdown(null);

  const handleGoToTeamTimesheets = () => {
    if (!managerId) return console.error("Manager ID not set");
    navigate(`/managertimesheetgrid/${managerId}`);
  };
  const handleGoToTimesheet = () => navigate("/managertimesheet", { state: { employeeId } });

  /* ── auth check ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in to access this page."); navigate("/"); return; }
    if (!roles.includes("ROLE_MANAGER")) { toast.error("You are not authorized to access this page."); navigate("/"); }
  }, [navigate]);

  /* ── appearance ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    const uid = localStorage.getItem("userId");
    if (!token || !uid) return;
    fetch(`${API_BASE_URL}/api/appearance/${uid}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (d?.navbarColor) setNavbarColor(d.navbarColor); })
      .catch(() => {});
  }, []);

  /* ── company logo ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    fetch(`${API_BASE_URL}/api/company-info`, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } })
      .then(r => r.json())
      .then(d => { if (Array.isArray(d) && d[0]?.logoPath) setCompanyLogo(`${API_BASE_URL}/${d[0].logoPath}`); })
      .catch(() => {});
  }, []);

  /* ── profile + photo ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    const eid = localStorage.getItem("employeeId");
    if (!token || !eid) return;

    fetch(`${API_BASE_URL}/api/users/employee/${eid}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        if (d) setProfile(p => ({
          ...p,
          username: d.name || p.username,
          roles: d.roles || ["ROLE_MANAGER"],
          role: d.roles?.includes("ROLE_MANAGER") ? "MANAGER" : p.role,
        }));
      }).catch(() => {});

    fetch(`${API_BASE_URL}/api/employees/me/photo`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        if (blob.size > 0) setProfile(p => ({ ...p, photo: URL.createObjectURL(blob) }));
        else setPhotoError(true);
      })
      .catch(() => setPhotoError(true));
  }, []);

  /* ── avatar ── */
  const Avatar = () => {
    if (!photoError && profile.photo)
      return <img src={profile.photo} alt="avatar" className="mnav-avatar" onError={() => setPhotoError(true)} />;
    return (
      <div className="mnav-avatar-fallback">
        {(profile.username || "M").charAt(0).toUpperCase()}
      </div>
    );
  };

  /* ── nav items ── */
  const navItems = [
    {
      key: "tasks", label: "Tasks", landing: "/managertasklanding",
      links: [{ label: "Task Management", href: "/viewtaskss" }],
    },
    {
      key: "timesheets", label: "Timesheets", landing: "/managertimesheetlanding",
      links: [
        { label: "Team Timesheet", onClick: (e) => { e.preventDefault(); handleGoToTeamTimesheets(); } },
        { label: "My Timesheet",   onClick: (e) => { e.preventDefault(); handleGoToTimesheet(); } },
      ],
    },
    {
      key: "leave", label: "Leave", landing: "/managerleavelanding",
      links: [
        { label: "Leave Approvals",   href: "/managerteamleaves" },
        { label: "Leave Application", href: "/managerleaveappli" },
        { label: "My Leaves",         href: "/managerleaves" },
      ],
    },
    {
      key: "team", label: "Team", landing: "/managerteamlanding",
      links: [{ label: "Team Management", href: "/teampage" }],
    },
    {
      key: "docs", label: "Documents", landing: "/managerdocumentlanding",
      links: [
        { label: "Upload Documents", href: "/manageruploaddocs" },
        { label: "My Documents",     href: "/managerdocs" },
      ],
    },
    {
      key: "details", label: "Details", landing: "/managerdetailslanding",
      links: [
        { label: "View Details",      onClick: (e) => { e.preventDefault(); navigate("/managerviewdetails", { state: { employeeId } }); } },
        { label: "Holiday Calendar",  href: "/mholidays" },
      ],
    },
  ];

  return (
    <div className="mnav">
      <style>{STYLES}</style>

      {/* ════════ TOP BAR ════════ */}
      <div className="mnav-topbar">
        {/* Left: logos */}
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <img src={logo} style={{ height: 39 }} alt="logo" />
          {companyLogo && (
            <img src={companyLogo} alt="company"
              style={{ height: 37, width: "auto", objectFit: "contain", borderRadius: 4, borderLeft: "2px solid #eef0f4", paddingLeft: 16 }} />
          )}
        </div>

        {/* Right: home + settings + profile */}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {/* Home */}
          <a href="/managerdashboard" className="mnav-home-btn" title="Home">
            <FaHome size={20} />
          </a>

          {/* Settings */}
          <a href="/managersettings" className="mnav-settings-btn">
            <FaSlidersH size={15} /> Settings
          </a>

          {/* Divider */}
          <div style={{ width: 1, height: 32, background: "#eef0f4", margin: "0 8px" }} />

          {/* Profile */}
          <div style={{ position: "relative" }} onMouseEnter={() => open("profile")} onMouseLeave={close}>
            <div className="mnav-profile-btn">
              <Avatar />
              <div>
                <div className="mnav-profile-name">{profile.username}</div>
                <div className="mnav-profile-role">
                  {activeRole?.replace("ROLE_", "")}
                </div>
              </div>
              <FaChevronDown size={10} style={{
                opacity: 0.5, marginLeft: 4, transition: "transform 0.2s",
                transform: openDropdown === "profile" ? "rotate(180deg)" : "none"
              }} />
            </div>

            {openDropdown === "profile" && (
              <div className="mnav-profile-drop">
                {/* header */}
                <div className="mnav-profile-header">
                  <div className="name">{profile.username}</div>
                  <span className="role-badge">{activeRole?.replace("ROLE_", "")}</span>
                </div>

                <a className="menu-item" href="/managerprofile">
                  <FaUserCog size={14} style={{ opacity: 0.7 }} /> Profile Settings
                </a>

                {/* Role switch */}
                {profile.roles?.length > 1 && (
                  <>
                    <div className="divider" />
                    <div style={{ padding: "8px 16px" }}>
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: "#9ea3bb", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 6 }}>
                        <FaExchangeAlt style={{ marginRight: 5, opacity: 0.7 }} />Switch Role
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {profile.roles.filter(r => r !== activeRole).map((role, i) => {
                          let path = "/";
                          if (role === "ROLE_MANAGER") path = "/managerdashboard";
                          if (role === "ROLE_ADMIN")   path = "/admindashboard";
                          if (role === "ROLE_USER")    path = "/empdashboard";
                          return (
                            <a key={i} className="role-pill" href={path}
                              onClick={e => {
                                e.preventDefault();
                                localStorage.setItem("activeRole", role);
                                setProfile(p => ({ ...p, role: role.replace("ROLE_", "") }));
                                navigate(path);
                              }}>
                              {role.replace("ROLE_", "")}
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}

                <div className="divider" />
                <div className="menu-item danger"
                  onClick={() => { sessionStorage.clear(); localStorage.clear(); navigate("/"); }}>
                  <FaSignOutAlt size={13} /> Logout
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ════════ MAIN NAV BAR ════════ */}
      <div className="mnav-bar" style={{ backgroundColor: navbarColor }}>
        <div style={{ display: "flex", alignItems: "center", flex: 1, gap: 2 }}>
          {navItems.map(item => (
            <div
              key={item.key}
              style={{ position: "relative" }}
              onMouseEnter={() => open(item.key)}
              onMouseLeave={close}
            >
              <div
                className={`mnav-item${openDropdown === item.key ? " active" : ""}`}
                onClick={() => navigate(item.landing)}
              >
                {item.label}
                <FaChevronDown className="chevron" />
              </div>
              {openDropdown === item.key && (
                <div className="mnav-drop">
                  {item.links.map((lnk, i) =>
                    lnk.onClick
                      ? <a key={i} href="#" onClick={lnk.onClick}>{lnk.label}</a>
                      : <a key={i} href={lnk.href}>{lnk.label}</a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="mnav-search">
          <FaSearch size={12} />
          <input
            type="text"
            placeholder="Search…"
            value={searchVal}
            onChange={e => setSearchVal(e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

export default ManagerNavbar;